<template>
  <div
    :class="{
      'hit-form-row': !disableRowClass,
      'floating-label-mode': floatingLabel,
    }"
  >
    <div
      v-if="(label || displayLabelColumn) && !floatingLabel"
      :class="{
        'inline-row': inlineInput,
        'help-visible': helpVisible,
        'hit-form-row-label': rowLabel,
        'label-normal-weight': labelNormalWeight,
        'center-label-vertically': !useCustomHeight,
        'position-label-vertically-via-margin': useCustomHeight,
        'align-left': labelAlignmentSide === 'left',
        'align-right': labelAlignmentSide === 'right',
        'align-center': labelAlignmentSide === 'center',
      }"
    >
      <div v-if="helpVisible" class="order-2 hit-form-help mt-0.5">
        <slot name="help">
          <hit-help v-if="help" :text="help" />
        </slot>
      </div>
      <label
        class="hit-form-label"
        :class="{
          'text-panel': !inlineInput || inlineLabelColorVisible,
          'text-panel-light': inlineInput && !inlineLabelColorVisible,
          'order-1': helpVisible,
        }"
      >
        {{ label }}
        <span v-if="pristine && required && invalid" class="text-danger"
          >*</span
        >
      </label>
    </div>
    <!-- There's a difference of 0.5 with showBottomMargin because, in mobile view, the label cuts off part of the input field when the bottom margin is 0.5 -->
    <div
      class="hit-form-row-input"
      :class="{
        'min-w-16': useMinWidth,
        'inline-row': inlineInput,
        'mb-3.5 xl:mb-3': showBottomMargin && !errorsVisible,
        'no-bottom-margin': !showBottomMargin || errorsVisible,
      }"
    >
      <div
        :class="{
          'w-full': !fullWidth,
          'height-8': !useCustomHeight,
          'min-height': setMinHeight,
          'compact-input-layout': compactInputLayout,
        }"
      >
        <slot />
        <span
          v-if="pristine && required && invalid && !label && !floatingLabel"
          class="text-danger ml-1"
          >*</span
        >
      </div>
    </div>
    <div
      v-if="errorsVisible"
      class="hit-form-row-errors"
      :class="{'mb-3': showBottomMargin}"
    >
      <slot name="customErrorMessage">
        <div
          v-for="error in validationErrors"
          :key="error.translationKey"
          class="text-danger-icon"
        >
          {{ t(error.translationKey, error.params) }}
        </div>
      </slot>
    </div>
  </div>
</template>

<script>
import {getValidationErrors} from '../../../models/hit/common/validation';
import HitFormValidationMixin from '../../../mixins/form/HitFormValidationMixin';
import HitInputMixin from '../../../mixins/form/HitInputMixin';
import {useI18n} from 'vue-i18n';

export default {
  name: 'HitBaseInput',
  components: {},
  mixins: [HitFormValidationMixin, HitInputMixin],
  inject: {
    hitTableInlineEditNoBottomMargin: {
      default: false,
    },
    /**
     * Provided by HitGroupInput. Children should not add their own bottom margin;
     * the group owns spacing to the next form sibling (gap inside, mb after).
     */
    hitInsideGroupInput: {
      default: false,
    },
  },
  props: {
    fullWidth: {
      type: Boolean,
      default: true,
    },
    rowLabel: {
      type: Boolean,
      default: true,
    },
    labelAlignmentSide: {
      type: String,
      required: false,
      default: 'right',
      validator: (value) => {
        return ['right', 'left', 'center'].indexOf(value) !== -1;
      },
    },
    setMinHeight: {
      type: Boolean,
      required: false,
      default: true,
    },
    onlyShowErrorBorder: {
      type: Boolean,
      required: false,
      default: false,
    },
    useMinWidth: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Label is rendered inside the field (floating); hides the side label column.
     */
    floatingLabel: {
      type: Boolean,
      default: false,
    },
    /**
     * Use compact input grid to avoid full-width column alignment.
     */
    compactInputLayout: {
      type: Boolean,
      default: false,
    },
    /**
     * Disable hit-form-row wrapper styles (display: contents + row grid behavior).
     */
    disableRowClass: {
      type: Boolean,
      default: false,
    },
    /**
     * Use normal (400) font weight on the side label instead of medium (500).
     */
    labelNormalWeight: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const {t} = useI18n();
    return {t};
  },
  computed: {
    validationErrors() {
      return getValidationErrors(this.validationState);
    },
    errorsVisible() {
      if (this.onlyShowErrorBorder) return false;
      // Floating-label fields: error is shown lifted on the control, not beside it
      if (this.floatingLabel) return false;
      return (
        this.validationErrors.length > 0 || !!this.$slots.customErrorMessage
      );
    },
    helpVisible() {
      return !!this.$slots['help'] || this.help;
    },
    showBottomMargin() {
      if (this.addBottomMargin === false) {
        return false;
      }
      if (this.addBottomMargin === true) {
        return true;
      }
      if (this.hitTableInlineEditNoBottomMargin) {
        return false;
      }
      // Inside a group: spacing comes from gap-y / the group's own margin.
      if (this.hitInsideGroupInput) {
        return false;
      }
      return true;
    },
    inputGridStyle() {
      return {
        'grid-template-rows': this.useCustomHeight ? '1fr' : '2.150rem',
        'grid-template-columns': 'minmax(0, 1fr)',
      };
    },
    cssProps() {
      return {
        '--first-column-width-inline-row': this.inlineLabelWidth,
      };
    },
    console() {
      return console;
    },
  },
  methods: {
    triggerValidations() {
      try {
        this.validationState.$touch();
      } catch {
        /** We simply try to trigger the validation **/
      }
    },
  },
  mounted() {
    this.triggerValidations();
  },
  watch: {
    validationState: {
      handler(newValue) {
        if (
          newValue &&
          newValue.$invalid &&
          newValue.$model != null &&
          !Array.isArray(newValue.$model)
        ) {
          newValue.$touch();
        }
      },
      immediate: true,
      // Need to watch deep, otherwise the watch won't compare the object properties
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
/* Unset any previous grid settings for the label and the input */
.hit-input-group {
  .hit-form-row {
    /* Keep each grouped field as a real box so errors cannot become grid columns */
    display: flex !important;
    flex-direction: column;
    min-width: 0;

    .hit-form-row-label {
      grid-column: unset;
    }

    .hit-form-row-input {
      grid-column: unset;
      min-width: 0;
    }

    &.floating-label-mode > .hit-form-row-errors {
      display: none !important;
    }
  }
}

.large-enough-for-form {
  /*
   * Form-grid participation (display:contents, width:100%, …) must be limited to
   * fields inside .hit-form. HitPanel sets large-enough-for-form on the whole panel,
   * so without this scope toolbar inputs (daily planning date + RoleSelect) also get
   * contents + width:100% and reflow each other when the select value changes.
   */
  .hit-form {
    .hit-form-row.floating-label-mode {
      .hit-form-row-input {
        grid-column: 1 / -1;
      }

      .hit-form-row-errors {
        grid-column: 1 / -1;
      }
    }

    .hit-form-row {
      @apply contents;

      .hit-form-help {
        @apply order-1;
      }

      .hit-form-label {
        white-space: nowrap;
        @apply order-2 text-right;
      }

      .hit-form-row-label {
        grid-column: 1;
        font-weight: 500;

        &.label-normal-weight {
          font-weight: 400;
        }

        &.center-label-vertically {
          display: flex;
          align-items: center;
        }

        &.position-label-vertically-via-margin {
          @apply justify-end mt-1.5;
        }

        &.inline-row {
          grid-column: unset;
        }
      }

      .hit-form-row-input {
        grid-column: 2;
        width: 100%;
        /* Form grid uses display:contents rows; spacing between fields. */
        // margin-bottom: 0.75rem;

        &.no-bottom-margin {
          margin-bottom: 0;
        }

        .height-8 {
          height: 2rem;
        }

        &.inline-row {
          grid-column: unset;
        }
      }

      .hit-form-row-errors {
        grid-column: 2;
        margin-left: 5px;
        @apply text-sm text-danger leading-none;
      }
    }

    .hit-form-row.floating-label-mode {
      .hit-form-row-errors {
        margin-left: 0;
      }
    }

    .hit-form-row + .hit-form-row:not(.floating-label-mode) {
      .hit-form-row-label:not(.inline-row),
      .hit-form-row-input:first-child:not(.inline-row) {
        @apply mt-0;
      }
    }

    .hit-form-row + .hit-form-row.floating-label-mode {
      .hit-form-row-input:not(.inline-row) {
        @apply mt-0;
      }
    }
  }
}

.hit-form-row {
  /* Standalone inputs (toolbars, calendars, …): real box so width stays content-sized. */
  @apply flex flex-col;

  .hit-form-row-label {
    @apply inline-grid gap-2;
    grid-template-columns: minmax(0, max-content);

    &.help-visible {
      grid-template-columns: minmax(0px, max-content) minmax(0px, max-content);
    }
  }

  .hit-form-row-input > div:first-child {
    @apply grid;
    /* minmax(0, 1fr): stable width with empty vs filled inputs (max-content shrinks with content) */
    grid-template-columns: minmax(0, 1fr) min-content;

    &.compact-input-layout {
      grid-template-columns: minmax(0, max-content);
    }

    &.height-8 {
      height: 2rem;
    }

    &.h-8 {
      grid-template-rows: 2rem;
    }
  }
}

.min-height {
  min-height: 2rem;
}
</style>

<!-- Shared floating-label visuals (used by HitInput, HitSelect, HitFastModeBrowser, …) -->
<style lang="scss">
/*
 * Horizontal inset for floating label + value text.
 * Label chip sits at --hit-floating-inset-x; lifted label uses px-1 (0.25rem),
 * so the value uses inset + 0.25rem to line up with the label text.
 */
.floating-label-mode,
.hit-floating-wrap {
  --hit-floating-inset-x: 0.3rem;
  --hit-floating-value-inset-x: calc(var(--hit-floating-inset-x) + 0.25rem);
}

.hit-floating-label {
  --hit-floating-lock-width: 2rem;
  position: absolute;
  left: var(--hit-floating-inset-x);
  z-index: 1;
  pointer-events: none;
  max-width: calc(100% - 0.875rem);
  display: flex;
  align-items: center;
  white-space: nowrap;
  // transition:
  //   transform 0.2s ease,
  //   color 0.2s ease,
  //   top 0.2s ease,
  //   font-size 0.2s ease;

  /* Ellipsis on the caption text only — keep help outside overflow:hidden */
  .hit-floating-label__text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Help sits after the label text inside the chip; keep clickable */
  .hit-floating-label__help {
    pointer-events: auto;
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    margin-left: 0.25rem;
    vertical-align: middle;
    line-height: 0;
  }

  /* Empty / unfocused: vertically center; same text start as the value */
  &.hit-floating-label--rest {
    top: 0;
    bottom: 0;
    padding-left: 0.25rem;
    justify-content: flex-start;
    transform: none;
    line-height: 1.2;
    @apply text-sm text-panel-light;
  }

  &.hit-floating-label--lifted {
    top: -0.12rem;
    bottom: auto;
    transform: translateY(-50%) scale(0.85);
    transform-origin: left top;
    font-size: 0.85rem;
    @apply rounded-sm bg-panel px-1 font-medium text-panel;
  }

  /* Validation message lifted on the border — same red as border-danger.
   * Kept here for non-muted fields; a stronger rule is repeated after muted styles. */
  &.hit-floating-label--error {
    color: var(--color-danger);
    -webkit-text-fill-color: var(--color-danger);
  }

  /* Keep lifted label aligned with the full control border when a left lock is shown. */
  &.hit-floating-label--with-left-lock.hit-floating-label--lifted {
    left: calc(var(--hit-floating-inset-x) - var(--hit-floating-lock-width));
    max-width: calc(100% - 0.875rem + var(--hit-floating-lock-width));
  }
}

/* Value text aligned with floating-label text (accounts for lifted px-1) */
.hit-floating-wrap > input,
.hit-floating-wrap > textarea {
  padding-left: var(--hit-floating-value-inset-x);
}

/* Multiline: rest caption at the top (placeholder position), not vertically centered. */
.hit-floating-wrap--textarea {
  .hit-floating-label--rest {
    top: 0.375rem;
    bottom: auto;
    height: auto;
    align-items: flex-start;
  }
}

.floating-label-mode .border-padding {
  padding-left: var(--hit-floating-value-inset-x);
}

.hit-floating-wrap--date {
  .hit-floating-label {
    left: calc(var(--hit-floating-value-inset-x) - 0.15rem);
  }
  .hit-floating-label--rest {
    padding-left: 0;
  }
  > input[type='date'],
  > input[type='datetime-local'] {
    padding-left: var(--hit-floating-value-inset-x);
    &::-webkit-datetime-edit,
    &::-webkit-datetime-edit-fields-wrapper {
      padding: 0;
      margin: 0;
    }
  }
}

/* Disabled / readonly: same look as editable fields, only lighter value text. */
.hit-input-muted-text,
.hit-input-muted-text:disabled,
.hit-input-muted-text[readonly],
.hit-input-muted-text .text-input,
.hit-input-muted-text .text-input-light,
.hit-input-muted-text .hit-select-input,
.hit-input-muted-text input,
.hit-input-muted-text textarea {
  opacity: 1 !important;
  cursor: not-allowed !important;
  color: color-mix(in srgb, var(--text-input-light) 55%, white) !important;
  -webkit-text-fill-color: color-mix(
    in srgb,
    var(--text-input-light) 55%,
    white
  ) !important;
}

.hit-input-muted-text::placeholder,
.hit-input-muted-text input::placeholder,
.hit-input-muted-text textarea::placeholder,
input.hit-input-muted-text::placeholder,
textarea.hit-input-muted-text::placeholder {
  opacity: 1 !important;
  color: color-mix(in srgb, var(--text-input-light) 55%, white) !important;
  -webkit-text-fill-color: color-mix(
    in srgb,
    var(--text-input-light) 55%,
    white
  ) !important;
}

input.hit-input-muted-text:disabled,
input.hit-input-muted-text[readonly],
textarea.hit-input-muted-text:disabled,
textarea.hit-input-muted-text[readonly],
select.hit-input-muted-text:disabled {
  background-color: var(--color-input) !important;
}

.hit-input-muted-text.text-transparent,
.hit-input-muted-text.text-transparent:disabled,
.hit-input-muted-text .text-transparent {
  color: transparent !important;
  -webkit-text-fill-color: transparent !important;
  caret-color: transparent;
}

.hit-input-muted-text.text-transparent::placeholder,
input.hit-input-muted-text.text-transparent::placeholder,
.hit-input-muted-text.text-transparent::-webkit-datetime-edit,
input.hit-input-muted-text.text-transparent::-webkit-datetime-edit,
.hit-input-muted-text.text-transparent::-moz-datetime-edit,
input.hit-input-muted-text.text-transparent::-moz-datetime-edit {
  color: transparent !important;
  -webkit-text-fill-color: transparent !important;
  opacity: 0 !important;
}

/*
 * Disabled/readonly floating labels:
 * - rest (empty): same light gray as muted value
 * - lifted: same as enabled fields
 */
.hit-floating-label--rest.hit-floating-label--muted,
.hit-input-muted-text .hit-floating-label--rest {
  color: color-mix(in srgb, var(--text-input-light) 55%, white) !important;
  -webkit-text-fill-color: color-mix(
    in srgb,
    var(--text-input-light) 55%,
    white
  ) !important;
}

.hit-input-muted-text .hit-floating-label--lifted {
  color: var(--text-panel) !important;
  -webkit-text-fill-color: var(--text-panel) !important;
}

/* After muted rules so danger wins; same red as border-danger */
.hit-floating-label.hit-floating-label--error,
.hit-floating-label.hit-floating-label--error.hit-floating-label--lifted {
  color: var(--color-danger) !important;
  -webkit-text-fill-color: var(--color-danger) !important;
}

/* Belt-and-suspenders: never show beside/below errors in floating-label mode */
.floating-label-mode > .hit-form-row-errors,
.hit-form-row.floating-label-mode .hit-form-row-errors {
  display: none !important;
}
</style>
